import React, { useState } from "react";
import { ClientApiProps } from "../../common";
import { DataGrid, ErrorScope, Label, Modal, Spinner, TextInput, useValidatedState } from "acma-spa";
import { Authorisation, AuthorisationToken, getApiClient } from "../../api";
import { RegisterStore } from "../../stores/RegisterStore";
import * as yup from "yup";

export interface ValidateTokenModalProps extends ClientApiProps {
  open: boolean
  onClose: () => any
  onChange: () => Promise<any>
}

interface Model {
  token: string,
  contactName: string,
  contactEmail: string
}

const schema: yup.ObjectSchema<Model> = yup.object({
  token: yup.string().required().label('Token'),
  contactName: yup.string().required().label('Contact name'),
  contactEmail: yup.string().email().required().label('Contact email')
});


export const ValidateTokenModal: React.FC<ValidateTokenModalProps> = (props) => {
  const [error, setError] = useState<string | undefined>(undefined);
  const [spinner, setSpinner] = useState<string>("");
  const [result, setResult] = useState<Authorisation[]>([]);

  const [model, setModel, errors, validate] = useValidatedState<Model>(() => ({
    token: '',
    contactName: '',
    contactEmail: ''
  }), schema);


  const onValidate = async () => {
    var res = await validate('#validateTokenContainer');
    if (!res) {
      return;
    }

    const [client, abort] = getApiClient(
      props.baseUrl,
      props.b2cDomainName,
      props.msalClient
    );
    const store = new RegisterStore(client);

    setSpinner("Requesting...");
    setError("");

    try {
      var data = await store.validateToken(new AuthorisationToken({
        token: model.token,
        contactName: model.contactName,
        contactEmail: model.contactEmail
      }));

      if (data.length > 0) {
        await props.onChange();
        setResult(data);
      }
      else {
        setError("Provided token is not valid");
      }
    }
    catch (e: any) {
      setError(e.message ? e.message : "Unexpected error");
    }
    finally {
      setSpinner("");
    }
  }

  return <>
    {props.open ? <Modal
      size="lg"
      id="validateTokenModal"
      open={true}
      onClose={() => !spinner && props.onClose()}
      title="Validate a token"
      onOpened={() => document.getElementById('alphaTags')?.focus()}
      keyboard={false}
      backdrop= "static"
      leftButtons={result.length == 0 ?[
        {
          label: "Validate",
          id: "validate",
          onClick: () => onValidate(),
          disabled: spinner ? true : false
        },
        {
          label: "Cancel",
          id: "cancel",
          onClick: () => props.onClose(),
          mode: 'secondary'
        }
      ] : [
          {
            label: "Close",
            id: "close",
            onClick: () => props.onClose(),
            mode: 'secondary'
          }
      ]}>
      <div id="validateTokenContainer" className="mb-2">
        <Spinner isLoading={spinner ? true : false} loadingText={spinner}>
          <Label id="token" label="Token" error={errors.token} hint="Insert received token" required>
            <TextInput value={model.token} onChange={v=> setModel({token: v})} maxLength={400} readonly={result.length > 0} />
          </Label>
          <Label id="contactName" label="Contact name" hint="Name of the person who presented this token" error={errors.contactName} required className="mt-3">
            <TextInput value={model.contactName} onChange={v => setModel({ contactName: v })} maxLength={400} readonly={result.length > 0} />
          </Label>
          <Label id="contactEmail" label="Contact email" hint="Email address of the person who presented this token" error={errors.contactEmail} required className="mt-3">
            <TextInput value={model.contactEmail} onChange={v => setModel({ contactEmail: v })} maxLength={400} readonly={result.length > 0} />
          </Label>

          {result.length > 0 ? <div className="mt-3">
            <DataGrid<Authorisation> columns={[
              { label: 'Confirmed alpha tag', value: r => r.name },
              { label: 'Company', value: r => r.clientName }
            ]} rows={ result} />
          </div> : <></>}
        </Spinner>
      </div>
      <ErrorScope id="error" error={error} />
    </Modal> : <></>}
  </>
}